<template>
    <button
        type="button"
        :class="[
            `icon-btn--${color}`, {
                'is--disabled': disabled
            }
        ]"
        :disabled="disabled"
        :aria-disabled="disabled"
        :data-test="dataTestName"
        :data-testid="dataTestName || 'button-icon'"
        :style="stylesAttribute"
        class="base-icon-btn"
        @click="handleClick"
    >
        <slot>
            <i
                v-if="icon.startsWith('vc-')"
                :class="[icon]"
                :style="{ fontSize: `${size}px` }"
                class=""
            />
            <redesign-base-icon
                v-else
                :name="icon"
                :fill="'transparent'"
                :size="size"
                class="icon-btn__icon"
            />
        </slot>
    </button>
</template>

<script setup lang="ts">
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { computed } from 'vue'

type IIconBtnColor = 'primary' | 'secondary' | 'destructive'

const props = withDefaults(
    defineProps<{
        icon: string,
        size?: number
        color?: IIconBtnColor
        hoverColor?: IIconBtnColor
        disabled?: boolean
        dataTestName?: string
    }>(),
    {
        color: 'secondary',
        hoverColor: undefined,
        size: 24,
        dataTestName: 'icon-base-button'
    })

const emit = defineEmits<{
    (e: 'click', pl: MouseEvent): void
}>()

const stylesAttribute = computed(() => {
    const color = props.hoverColor || props.color
    return  {
        '--hover-color-name': `var(--${color}-actions)`
    }
})
function handleClick (evt: MouseEvent): void {
    if (!props.disabled) {
        emit('click', evt)
    }
}
</script>

<style lang="scss">
    .base-icon-btn {
        --hover-color: color-mix(in srgb, var(--hover-color-name, var(--primary-actions)) 90%, #000);
        @apply
        relative
        outline-none
        inline-flex justify-center items-center flex-shrink-0
        m-0
        rounded-sm
        transition-all duration-200
        w-auto h-max p-0.5;

        &.icon-btn--primary {
            @apply text-primary-actions;
            &:not(.is--disabled):hover {
                color: var(--hover-color);
            }
        }
        &.icon-btn--secondary {
            @apply text-secondary-actions;
            &:not(.is--disabled):hover {
                color: var(--hover-color);
            }
        }
        &.icon-btn--destructive {
            @apply text-destructive-actions;
            &:not(.is--disabled):hover {
                color: var(--hover-color);
            }
        }

        &:focus-visible {
            @apply ring-1 ring-current;
        }


        &.is--disabled {
            @apply opacity-50 cursor-not-allowed;
        }
    }
</style>
